import React, {useEffect} from 'react';
import {Navigate, useParams} from 'react-router-dom';
import EmberIframe from '@/components/ember-iframe';
import PoppaysLoader from '@/components/poppays-loader';
import usePaidGigDetailQuery from '@/hooks/queries/gigs/use-paid-gig-detail-query';
import GigDeliverablesPage from './GigDeliverablesPage';
import AffiliateIncomePage from './affiliate';
import GigApplicationPage from './application';
import GigBriefPage from './brief';
import PrepPage from './prep';
import GigLoadError from './components/GigLoadError';
import {ACCEPTED_STATES} from './shell/GigShell';
import {trackDeliverables, captureDeliverablesError} from './telemetry';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const EMBER_GIG_SEGMENTS = new Set([
  'posts',
  'reels',
  'stories',
  'content-submission',
  'conversations',
]);

/**
 * An unaccepted gig goes to Your Application, mirroring Ember's
 * `activity/index.js:10`. The upload-deliverables check only distinguishes surfaces
 * once a gig is accepted; on its own it sent a rejected gig to the deliverables page
 * whenever its campaign happened to require uploads. Ember skips this redirect on
 * mobile, where its index acts as a menu; the shell renders tabs on every viewport,
 * so here it is unconditional.
 */
function GigIndexGate({gigId}: {gigId: string}) {
  const {
    deliverables,
    uploadCounts,
    shell,
    isLoading,
    isError,
    error,
    refetch,
  } = usePaidGigDetailQuery(gigId);

  const inAcceptedState = ACCEPTED_STATES.has(shell.state);
  const hasUploadDeliverables =
    deliverables.length > 0 ||
    Object.values(uploadCounts ?? {}).some((count) => count > 0);

  let surface: string;
  if (!inAcceptedState) {
    surface = 'react-application';
  } else if (hasUploadDeliverables) {
    surface = 'react-deliverables';
  } else {
    surface = 'ember';
  }

  useEffect(() => {
    if (isLoading) return;
    if (isError) {
      captureDeliverablesError(error, 'Gig detail failed to load', {gigId});
      return;
    }
    trackDeliverables('Resolved gig index', {gigId, surface});
  }, [isLoading, isError, error, surface, gigId]);

  if (isLoading) {
    return <PoppaysLoader />;
  }
  if (isError) {
    return <GigLoadError onRetry={refetch} />;
  }
  if (!inAcceptedState) {
    return <Navigate to={`/gigs/${gigId}/proposal`} replace />;
  }

  return hasUploadDeliverables ? <GigDeliverablesPage /> : <EmberIframe />;
}

/**
 * Decides, within the `/gigs/*` space, whether to render the React deliverables
 * experience or fall through to the Ember page. Only real gigs (UUID id) are
 * candidates — the gig lists (`/gigs`, `/gigs/your-gigs[/:scope]`) keep `gigId`
 * non-UUID and stay on Ember.
 */
export default function GigDeliverablesGate() {
  const params = useParams();
  const gigId = params.gigId ?? '';
  const firstSegment = (params['*'] ?? '').split('/')[0];

  if (!UUID_RE.test(gigId)) {
    return <EmberIframe />;
  }
  if (firstSegment === '') {
    return <GigIndexGate gigId={gigId} />;
  }
  if (firstSegment === 'brief') {
    return <GigBriefPage />;
  }
  if (firstSegment === 'proposal') {
    return <GigApplicationPage />;
  }
  if (firstSegment === 'prep') {
    return <PrepPage />;
  }
  if (firstSegment === 'affiliate-income') {
    return <AffiliateIncomePage />;
  }
  if (EMBER_GIG_SEGMENTS.has(firstSegment)) {
    return <EmberIframe />;
  }
  // A content-submission kind page (photos/videos/blogs/mixed-media, incl. platform-prefixed).
  return <GigDeliverablesPage />;
}
